import { useState, useEffect } from 'react';
import { getDocuments, uploadDocument, deleteDocument, scrapeUrl, scrapeMultipleUrls } from '../../services/api';
import { useToast } from '../../hooks/useToast';
import { ToastContainer } from '../Shared/Toast';
import './AdminDocuments.css';

const AdminDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [filter, setFilter] = useState('all');
  const [urlInput, setUrlInput] = useState('');
  const [urlTitle, setUrlTitle] = useState('');
  const [scrapingUrl, setScrapingUrl] = useState(false);
  const [multipleUrlsMode, setMultipleUrlsMode] = useState(false);
  const [urlsTextarea, setUrlsTextarea] = useState('');
  const toast = useToast();

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const data = await getDocuments();
      const docsList = Array.isArray(data) ? data : (data.results || []);
      setDocuments(docsList);
    } catch (error) {
      console.error('Failed to load documents:', error);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMultipleFileUpload = async (files) => {
    if (!files || files.length === 0) return;

    const allowedTypes = ['.pdf', '.docx', '.txt', '.md'];
    const maxSize = 50 * 1024 * 1024;

    const validFiles = [];
    for (let file of files) {
      const fileExt = '.' + file.name.split('.').pop().toLowerCase();
      if (!allowedTypes.includes(fileExt)) {
        toast.showError(`${file.name}: File type not allowed. Allowed types: ${allowedTypes.join(', ')}`);
        continue;
      }
      if (file.size > maxSize) {
        toast.showError(`${file.name}: File size exceeds 50MB limit`);
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length === 0) return;

    const newUploadingFiles = validFiles.map((file, index) => ({
      id: Date.now() + index,
      name: file.name,
      file: file,
      progress: 0,
      status: 'uploading'
    }));

    setUploadingFiles(prev => [...prev, ...newUploadingFiles]);

    for (let uploadingFile of newUploadingFiles) {
      try {
        const progressInterval = setInterval(() => {
          setUploadingFiles(prev =>
            prev.map(f =>
              f.id === uploadingFile.id && f.progress < 90
                ? { ...f, progress: f.progress + 10 }
                : f
            )
          );
        }, 200);

        await uploadDocument(uploadingFile.file);

        clearInterval(progressInterval);

        setUploadingFiles(prev =>
          prev.map(f =>
            f.id === uploadingFile.id
              ? { ...f, progress: 100, status: 'success' }
              : f
          )
        );

        toast.showSuccess(`${uploadingFile.name} uploaded successfully!`);

      } catch (error) {
        console.error('Failed to upload document:', error);
        setUploadingFiles(prev =>
          prev.map(f =>
            f.id === uploadingFile.id
              ? { ...f, status: 'error' }
              : f
          )
        );
        toast.showError(`${uploadingFile.name} failed to upload`);
      }
    }

    await loadDocuments();
    setTimeout(() => {
      setUploadingFiles(prev =>
        prev.filter(f => f.status !== 'success' && f.status !== 'error')
      );
    }, 2000);
  };

  const handleUrlScrape = async (e) => {
    e.preventDefault();

    if (multipleUrlsMode) {
      if (!urlsTextarea.trim()) {
        toast.showError('Please enter at least one URL');
        return;
      }

      setScrapingUrl(true);

      try {
        const urlLines = urlsTextarea.split('\n').filter(line => line.trim());
        const urls = urlLines.map(line => {
          const parts = line.split('|').map(p => p.trim());
          return {
            url: parts[0],
            title: parts[1] || ''
          };
        });

        for (const urlObj of urls) {
          try {
            new URL(urlObj.url);
          } catch {
            toast.showError(`Invalid URL format: ${urlObj.url}`);
            return;
          }
        }

        if (urls.length > 50) {
          toast.showError('Maximum 50 URLs at once');
          return;
        }

        const result = await scrapeMultipleUrls(urls);

        const successCount = result.summary.successful;
        const failCount = result.summary.failed;

        if (successCount > 0) {
          toast.showSuccess(
            `Processed ${successCount} URLs successfully! ${failCount} failed.`
          );
        }

        if (failCount > 0) {
          const failed = result.results.filter(r => !r.success);
          const errors = failed.map(f => `${f.url}: ${f.error}`).join('\n');
          console.error('Failed URLs:\n' + errors);
        }

        setUrlsTextarea('');
        setMultipleUrlsMode(false);
        await loadDocuments();
      } catch (error) {
        console.error('Failed to scrape URLs:', error);
        const errorMsg = error.response?.data?.error || error.response?.data?.detail || 'Failed to scrape URLs';
        toast.showError(errorMsg);
      } finally {
        setScrapingUrl(false);
      }
    } else {
      if (!urlInput.trim()) {
        toast.showError('Please enter a URL');
        return;
      }

      try {
        new URL(urlInput);
      } catch {
        toast.showError('Invalid URL format');
        return;
      }

      setScrapingUrl(true);

      try {
        const result = await scrapeUrl(urlInput, urlTitle);

        toast.showSuccess(
          `URL scraped successfully! ${result.stats.chunks_created} chunks created from ${result.stats.content_length} characters`
        );

        setUrlInput('');
        setUrlTitle('');
        await loadDocuments();
      } catch (error) {
        console.error('Failed to scrape URL:', error);
        const errorMsg = error.response?.data?.error || error.response?.data?.detail || 'Failed to scrape URL';
        toast.showError(errorMsg);
      } finally {
        setScrapingUrl(false);
      }
    }
  };

  const handleDelete = async (docId, docTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${docTitle}"? This will affect all users.`)) {
      return;
    }

    try {
      await deleteDocument(docId);
      await loadDocuments();
      toast.showSuccess(`"${docTitle}" deleted successfully!`);
    } catch (error) {
      console.error('Failed to delete document:', error);
      toast.showError('Failed to delete document. Please try again.');
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleMultipleFileUpload(Array.from(e.dataTransfer.files));
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const filteredDocuments = documents.filter(doc => {
    if (filter === 'all') return true;
    if (filter === 'processed') return doc.is_processed;
    if (filter === 'processing') return !doc.is_processed;
    return true;
  });

  if (loading) {
    return (
      <div className="docs-page-loading">
        <div className="loading-spinner-docs">
          <div></div><div></div><div></div><div></div>
        </div>
        <p>Loading documents...</p>
      </div>
    );
  }

  return (
    <div className="docs-page"
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
    >
      {/* Stats Header */}
      <div className="docs-stats-header">
        <div className="docs-stat-box">
          <div className="stat-icon-box total">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
            </svg>
          </div>
          <div className="stat-text">
            <span className="stat-num">{documents.length}</span>
            <span className="stat-lbl">Total</span>
          </div>
        </div>
        <div className="docs-stat-box">
          <div className="stat-icon-box processed">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
          </div>
          <div className="stat-text">
            <span className="stat-num">{documents.filter(d => d.is_processed).length}</span>
            <span className="stat-lbl">Processed</span>
          </div>
        </div>
        <div className="docs-stat-box">
          <div className="stat-icon-box pending">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
          </div>
          <div className="stat-text">
            <span className="stat-num">{documents.filter(d => !d.is_processed).length}</span>
            <span className="stat-lbl">Processing</span>
          </div>
        </div>
      </div>

      {/* Upload Actions */}
      <div className="upload-actions-grid">
        {/* File Upload Card */}
        <div className="upload-action-card">
          <input
            type="file"
            id="admin-file-upload"
            accept=".pdf,.docx,.txt,.md"
            multiple
            onChange={(e) => handleMultipleFileUpload(Array.from(e.target.files))}
            disabled={uploadingFiles.length > 0}
            style={{ display: 'none' }}
          />
          <label htmlFor="admin-file-upload" className={`upload-drop-zone ${dragActive ? 'active' : ''}`}>
            <div className="upload-icon">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
            </div>
            <h4>Upload Documents</h4>
            <p>Drag & drop files or click to browse</p>
            <span className="upload-hint">PDF, DOCX, TXT, MD (max 50MB)</span>
          </label>
        </div>

        {/* URL Scrape Card */}
        <div className="upload-action-card scrape-card">
          <div className="scrape-header">
            <div className="scrape-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="12" r="10"/>
                <line x1="2" y1="12" x2="22" y2="12"/>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
              </svg>
            </div>
            <div className="scrape-title">
              <h4>Scrape Website</h4>
              <p>Import content from URLs</p>
            </div>
          </div>

          <div className="mode-toggle">
            <button
              type="button"
              className={`mode-btn ${!multipleUrlsMode ? 'active' : ''}`}
              onClick={() => setMultipleUrlsMode(false)}
              disabled={scrapingUrl}
            >
              Single URL
            </button>
            <button
              type="button"
              className={`mode-btn ${multipleUrlsMode ? 'active' : ''}`}
              onClick={() => setMultipleUrlsMode(true)}
              disabled={scrapingUrl}
            >
              Multiple URLs
            </button>
          </div>

          <form onSubmit={handleUrlScrape} className="scrape-form">
            {multipleUrlsMode ? (
              <div className="form-field">
                <textarea
                  placeholder="https://www.nhs.uk/conditions/... | Title&#10;https://www.gov.uk/health/...&#10;https://www.example.com/..."
                  value={urlsTextarea}
                  onChange={(e) => setUrlsTextarea(e.target.value)}
                  disabled={scrapingUrl}
                  rows="4"
                />
                <span className="field-hint">One URL per line. Optionally add title after |</span>
              </div>
            ) : (
              <>
                <div className="form-field">
                  <input
                    type="url"
                    placeholder="https://www.nhs.uk/conditions/..."
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    disabled={scrapingUrl}
                    required
                  />
                </div>
                <div className="form-field">
                  <input
                    type="text"
                    placeholder="Custom title (optional)"
                    value={urlTitle}
                    onChange={(e) => setUrlTitle(e.target.value)}
                    disabled={scrapingUrl}
                  />
                </div>
              </>
            )}
            <button
              type="submit"
              disabled={scrapingUrl || (multipleUrlsMode ? !urlsTextarea.trim() : !urlInput.trim())}
              className="scrape-submit-btn"
            >
              {scrapingUrl ? (
                <>
                  <span className="btn-spinner"></span>
                  Scraping...
                </>
              ) : (
                <>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                  {multipleUrlsMode ? 'Scrape All URLs' : 'Scrape & Import'}
                </>
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Upload Progress */}
      {uploadingFiles.length > 0 && (
        <div className="upload-progress-section">
          <h4>Uploading Files</h4>
          <div className="upload-progress-list">
            {uploadingFiles.map((file) => (
              <div key={file.id} className={`upload-progress-item ${file.status}`}>
                <div className="file-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/>
                    <polyline points="13 2 13 9 20 9"/>
                  </svg>
                </div>
                <div className="file-info">
                  <span className="file-name">{file.name}</span>
                  <div className="progress-track">
                    <div className="progress-fill" style={{ width: `${file.progress}%` }}></div>
                  </div>
                </div>
                <span className="progress-percent">{file.progress}%</span>
                {file.status === 'success' && (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#10a37f" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                )}
                {file.status === 'error' && (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="15" y1="9" x2="9" y2="15"/>
                    <line x1="9" y1="9" x2="15" y2="15"/>
                  </svg>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="filter-section">
        <div className="filter-tabs">
          <button
            className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All
            <span className="tab-count">{documents.length}</span>
          </button>
          <button
            className={`filter-tab ${filter === 'processed' ? 'active' : ''}`}
            onClick={() => setFilter('processed')}
          >
            Processed
            <span className="tab-count">{documents.filter(d => d.is_processed).length}</span>
          </button>
          <button
            className={`filter-tab ${filter === 'processing' ? 'active' : ''}`}
            onClick={() => setFilter('processing')}
          >
            Processing
            <span className="tab-count">{documents.filter(d => !d.is_processed).length}</span>
          </button>
        </div>
      </div>

      {/* Documents Table */}
      <div className="documents-table-container">
        {filteredDocuments.length === 0 ? (
          <div className="docs-empty-state">
            <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
            </svg>
            <h3>No documents found</h3>
            <p>Upload files or scrape websites to build your knowledge base</p>
          </div>
        ) : (
          <table className="documents-table">
            <thead>
              <tr>
                <th>Document</th>
                <th>Type</th>
                <th>Size</th>
                <th>Status</th>
                <th>Uploaded</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredDocuments.map((doc) => (
                <tr key={doc.id}>
                  <td className="doc-name-cell">
                    <div className="doc-icon-sm">
                      {doc.file_type === 'url' ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"/>
                          <line x1="2" y1="12" x2="22" y2="12"/>
                          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                        </svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                          <polyline points="14 2 14 8 20 8"/>
                        </svg>
                      )}
                    </div>
                    <span className="doc-title-text">{doc.title}</span>
                  </td>
                  <td>
                    <span className="type-badge">{doc.file_type.toUpperCase()}</span>
                  </td>
                  <td className="size-cell">{formatFileSize(doc.file_size)}</td>
                  <td>
                    <span className={`status-pill ${doc.is_processed ? 'ready' : 'pending'}`}>
                      {doc.is_processed ? 'Ready' : 'Processing'}
                    </span>
                  </td>
                  <td className="date-cell">{formatDate(doc.uploaded_at)}</td>
                  <td>
                    <button
                      className="delete-btn"
                      onClick={() => handleDelete(doc.id, doc.title)}
                      title="Delete document"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Drag Overlay */}
      {dragActive && (
        <div className="drag-overlay">
          <div className="drag-content">
            <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            <p>Drop files to upload</p>
          </div>
        </div>
      )}

      <ToastContainer toasts={toast.toasts} removeToast={toast.removeToast} />
    </div>
  );
};

export default AdminDocuments;
